import { createContext, useContext, useEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "theme";
export const DENSITY_STORAGE_KEY = "aw_density";
export const THEMES = ["light", "dark", "system"];
export const DENSITIES = ["default", "compact"];

const getSystemTheme = () => (
  typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light"
);

const readPreference = (key, allowed, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const ThemeContext = createContext({
  theme: "dark",
  resolvedTheme: "dark",
  setTheme: () => {},
  density: "default",
  setDensity: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => readPreference(THEME_STORAGE_KEY, THEMES, "dark"));
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const [density, setDensity] = useState(() => readPreference(DENSITY_STORAGE_KEY, DENSITIES, "default"));
  const resolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return undefined;
    const updateSystemTheme = () => setSystemTheme(media.matches ? "dark" : "light");
    updateSystemTheme();
    media.addEventListener?.("change", updateSystemTheme);
    return () => media.removeEventListener?.("change", updateSystemTheme);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
    root.dataset.theme = resolvedTheme;
    try { window.localStorage.setItem(THEME_STORAGE_KEY, theme); } catch {}

    const themeColor = resolvedTheme === "dark" ? "#101219" : "#f8fafd";
    window.document.querySelector('meta[name="theme-color"]')?.setAttribute("content", themeColor);
  }, [theme, resolvedTheme]);

  useEffect(() => {
    const root = window.document.documentElement;
    if (density === "compact") root.dataset.density = "compact";
    else delete root.dataset.density;
    try { window.localStorage.setItem(DENSITY_STORAGE_KEY, density); } catch {}
  }, [density]);

  const value = useMemo(() => ({
    theme,
    resolvedTheme,
    setTheme: (nextTheme) => setTheme(THEMES.includes(nextTheme) ? nextTheme : "dark"),
    density,
    setDensity: (nextDensity) => setDensity(DENSITIES.includes(nextDensity) ? nextDensity : "default"),
  }), [theme, resolvedTheme, density]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
